import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Calendar } from "./ui/calendar";
import { toast } from "sonner";
import { CalendarDays, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { SERVICES, BRAND, TIME_SLOTS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOCATION_OPTS = BRAND.locations.map((l) => l.city).concat(["Remote / Other"]);

const todayMidnight = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const toISODate = (d) => {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function Booking() {
  const [date, setDate] = useState(undefined);
  const [timeSlot, setTimeSlot] = useState("");
  const [taken, setTaken] = useState([]);
  const [loadingTaken, setLoadingTaken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "Kuala Lumpur",
    service: SERVICES[0].title,
    notes: "",
  });

  useEffect(() => {
    if (!date) {
      setTaken([]);
      return;
    }
    const iso = toISODate(date);
    setLoadingTaken(true);
    axios
      .get(`${API}/bookings/taken`, { params: { date: iso } })
      .then((r) => setTaken(r.data?.taken || []))
      .catch(() => setTaken([]))
      .finally(() => setLoadingTaken(false));
  }, [date]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date || !timeSlot) {
      toast.error("Please pick a date and time slot.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/bookings`, {
        ...form,
        date: toISODate(date),
        time_slot: timeSlot,
      });
      toast.success("Booking received! We'll confirm via email within 24h.");
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Could not submit booking. Try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAfterSuccess = () => {
    setDone(false);
    setDate(undefined);
    setTimeSlot("");
    setForm({
      name: "",
      email: "",
      phone: "",
      location: "Kuala Lumpur",
      service: SERVICES[0].title,
      notes: "",
    });
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-14">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B30] mb-4">
            — Book a consult
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.9]">
            Pick a date.
            <br />
            <span className="text-outline-red">We'll bring the ideas.</span>
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed">
            30‑minute free discovery call with our strategy team. On‑site in
            Penang / KL / Johor, or over Google Meet.
          </p>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="booking-success"
            className="glass p-12 text-center"
          >
            <CheckCircle2 size={48} className="text-[#FF3B30] mx-auto" />
            <h3 className="mt-6 font-display text-3xl md:text-4xl font-black uppercase">
              You're in.
            </h3>
            <p className="mt-4 text-white/70 max-w-md mx-auto">
              We've got your booking for{" "}
              <span className="text-white font-semibold">{toISODate(date)}</span>{" "}
              at <span className="text-white font-semibold">{timeSlot}</span>.
              Expect a confirmation email within 24 hours.
            </p>
            <button
              data-testid="booking-reset"
              onClick={resetAfterSuccess}
              className="btn-ghost mt-8"
            >
              Book another slot
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Calendar */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5 glass p-6 md:p-8"
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/50 mb-5">
                <CalendarDays size={14} /> Select a date
              </div>
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => {
                  const day = d.getDay();
                  return d < todayMidnight() || day === 0 || day === 6;
                }}
                initialFocus
                data-testid="booking-calendar"
              />

              <div className="hline my-6" />

              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/50 mb-4">
                <Clock size={14} /> Available time slots
              </div>
              {!date ? (
                <p className="text-sm text-white/40">
                  Pick a date to see available slots.
                </p>
              ) : loadingTaken ? (
                <div className="text-white/50 text-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Checking availability…
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {TIME_SLOTS.map((t) => {
                    const disabled = taken.includes(t);
                    const active = timeSlot === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        disabled={disabled}
                        onClick={() => setTimeSlot(t)}
                        data-testid={`slot-${t.replace(/\s|:/g, "")}`}
                        className={`text-[11px] md:text-xs uppercase tracking-[0.15em] py-3 border transition-all ${
                          active
                            ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                            : disabled
                            ? "border-white/5 text-white/20 line-through cursor-not-allowed"
                            : "border-white/10 text-white/70 hover:border-[#FF3B30] hover:text-white"
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}
            </motion.div>

            {/* Form */}
            <motion.form
              onSubmit={onSubmit}
              data-testid="booking-form"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-7 glass p-6 md:p-10 flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Your name" testId="booking-name">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    placeholder="Aisyah Tan"
                  />
                </Field>
                <Field label="Email" testId="booking-email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputCls}
                    placeholder="you@brand.com"
                  />
                </Field>
                <Field label="Phone" testId="booking-phone">
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className={inputCls}
                    placeholder="+60 12 345 6789"
                  />
                </Field>
                <Field label="Preferred location" testId="booking-location">
                  <select
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className={inputCls}
                  >
                    {LOCATION_OPTS.map((l) => (
                      <option key={l} value={l} className="bg-[#111]">
                        {l}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Service focus" testId="booking-service">
                  <select
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                    className={`${inputCls} md:col-span-2`}
                  >
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title} className="bg-[#111]">
                        {s.title}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Notes / project brief" testId="booking-notes">
                <textarea
                  rows={4}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className={`${inputCls} resize-none`}
                  placeholder="A quick line about what you're building…"
                />
              </Field>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <div className="text-xs text-white/50">
                  {date ? (
                    <>
                      <span className="text-white">{toISODate(date)}</span>
                      {timeSlot && <> · <span className="text-[#FF3B30]">{timeSlot}</span></>}
                    </>
                  ) : (
                    "No date selected"
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit"
                  className="btn-primary inline-flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  {submitting ? "Booking…" : "Confirm booking"}
                </button>
              </div>
            </motion.form>
          </div>
        )}
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-black/40 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-[#FF3B30] focus:outline-none transition-colors";

function Field({ label, children, testId }) {
  return (
    <label className="flex flex-col gap-2" data-testid={testId}>
      <span className="text-[11px] uppercase tracking-[0.25em] text-white/50">
        {label}
      </span>
      {children}
    </label>
  );
}
